<template>
  <div data-test-id="link-session-modal">
    <DialogHeader>
      <DialogTitle>{{ t('sessionObjectives.linkSessionModal.title') }}</DialogTitle>
    </DialogHeader>

    <form class="mt-6 flex flex-col gap-6" @submit.prevent="onSubmit">
      <div v-if="isLoading" class="flex items-center justify-center gap-2 py-10 text-muted-foreground">
        <Spinner />
        <span>{{ t('sessionObjectives.linkSessionModal.loading') }}</span>
      </div>

      <div v-else-if="error" class="py-10 text-center text-destructive">
        {{ t('sessionObjectives.linkSessionModal.error') }}
      </div>

      <div v-else-if="sessions.length === 0" class="py-10 text-center text-muted-foreground">
        {{ t('sessionObjectives.linkSessionModal.noSessions') }}
      </div>

      <FormField v-else v-slot="{ componentField }" name="sessionId">
        <FormItem>
          <FormLabel>{{ t('sessionObjectives.linkSessionModal.selectLabel') }}</FormLabel>
          <Select v-bind="componentField">
            <FormControl>
              <SelectTrigger data-test-id="link-session-select" class="w-full">
                <SelectValue :placeholder="t('sessionObjectives.linkSessionModal.selectLabel')" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem
                v-for="option in sessionOptionsRef"
                :key="option.value"
                :value="option.value"
                data-test-id="popover-row"
              >
                {{ option.label }}
              </SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      </FormField>

      <DialogFooter>
        <Button type="button" variant="outline" @click="closeModal" data-test-id="link-session-cancel-button">
          {{ t('sessionObjectives.linkSessionModal.cancelButton') }}
        </Button>
        <Button
          type="submit"
          :disabled="!values.sessionId || linkMutation.isPending.value || sessions.length === 0"
          data-test-id="link-session-confirm-button"
        >
          <Spinner v-if="linkMutation.isPending.value" />
          {{ t('sessionObjectives.linkSessionModal.linkButton') }}
        </Button>
      </DialogFooter>
    </form>
  </div>
</template>

<script setup lang="ts">
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { computed, watch } from 'vue'
import { toast } from 'vue-sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import {
  selectGetTempSelectedGroupId,
  selectGetTempSelectedObjectiveId,
} from '@/features/groups/stores/groups/groups.selectors'
import groupsStore from '@/features/groups/stores/groups/groups.store'
import { linkSessionToObjective, SESSION_OBJECTIVES_QUERY_KEY } from '@/features/programs/api/session-objectives.api'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import { getSessions, V1_SESSIONS_QUERY_KEY } from '@/features/sessions/api'
import { selectGetLinkSessionToObjectiveModal } from '@/features/ui/store/modal/modal.selectors'
import modalsStore from '@/features/ui/store/modal/modal.store'
import dayjsClient from '@/lib/dayjs'
import { useI18n } from '@/lib/i18n'

const { t } = useI18n()
const queryClient = useQueryClient()

const linkSessionToObjectiveModalRef = selectGetLinkSessionToObjectiveModal(modalsStore)
const tempSelectedObjectiveIdRef = selectGetTempSelectedObjectiveId(groupsStore)
const tempSelectedGroupIdRef = selectGetTempSelectedGroupId(groupsStore)
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)

const groupId = computed(() => tempSelectedGroupIdRef.value)
const objectiveId = computed(() => tempSelectedObjectiveIdRef.value)

const programId = computed(() => {
  const attachedData = linkSessionToObjectiveModalRef.value.attachedData as { programId?: string } | undefined
  return attachedData?.programId || selectedProgramIdRef.value || ''
})

const {
  data: sessionsData,
  isLoading,
  error,
} = useQuery({
  queryKey: [V1_SESSIONS_QUERY_KEY, 'group', groupId, programId],
  queryFn: async () => {
    if (!groupId.value || !programId.value) {
      return { sessions: [], totalSessions: 0, totalPages: 0 }
    }
    return getSessions({
      programId: programId.value,
      periodId: '',
      groupId: groupId.value,
      page: 1,
      perPage: 100,
      filterEmail: '',
    })
  },
  enabled: computed(() => !!groupId.value && !!programId.value && linkSessionToObjectiveModalRef.value.isOpen),
})

const sessions = computed(() => sessionsData.value?.sessions ?? [])

const sessionOptionsRef = computed(() =>
  sessions.value.map((session) => ({
    value: session.id,
    label: `${session.name || t('common.session.capitalize')} - ${dayjsClient(session.from).format('DD/MM/YYYY HH:mm')}`,
  })),
)

const formSchema = toTypedSchema(
  z.object({
    sessionId: z
      .string({ required_error: t('sessionObjectives.linkSessionModal.selectLabel') })
      .min(1, t('sessionObjectives.linkSessionModal.selectLabel')),
  }),
)

const { handleSubmit, values, resetForm } = useForm({
  validationSchema: formSchema,
  initialValues: { sessionId: undefined },
})

const linkMutation = useMutation({
  mutationFn: (input: { objectiveId: string; sessionId: string }) =>
    linkSessionToObjective(input.objectiveId, input.sessionId),
  onError: () => {
    toast?.error(t('error.common'))
  },
  onSuccess: () => {
    toast?.success(t('sessionObjectives.linkSessionModal.success'))
    queryClient.invalidateQueries({ queryKey: [SESSION_OBJECTIVES_QUERY_KEY] })
    queryClient.invalidateQueries({ queryKey: [V1_SESSIONS_QUERY_KEY] })
    modalsStore.toggleModal('link-session-to-objective', false)
    groupsStore.setTempSelectedObjectiveId(null)
    resetForm()
  },
})

function closeModal() {
  modalsStore.toggleModal('link-session-to-objective', false)
}

const onSubmit = handleSubmit((data) => {
  if (!objectiveId.value) return
  linkMutation.mutate({ objectiveId: objectiveId.value, sessionId: data.sessionId })
})

watch(
  () => linkSessionToObjectiveModalRef.value.isOpen,
  (isOpen) => {
    if (!isOpen) {
      groupsStore.setTempSelectedObjectiveId(null)
      resetForm()
    }
  },
)
</script>
